import type {
  BaseServiceOptions,
  Pagination,
  TRecordAction,
} from '@/core/types'
import {
  type TBundle,
  serviceConfig,
  moduleServiceDailyReportHandlers,
  moduleServiceHandlers,
  coilStocksHandlers,
  reportsServiceHandlers,
  TDailyReport,
  TDailyReports,
} from '../../..'
import { useNotification, useServiceHandlers, defaultPagination } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { TCoils } from '@/modules/receptions'

const { model } = serviceConfig.logsProduction

export const useDailyReportQuery = (
  options: BaseServiceOptions<Pagination>,
) => {
  const { currentPage, filters, modelId, enableQuery, modelChildId, handlers } =
    options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TDailyReport>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const loadRecords = useQuery<TDailyReports, AxiosError>({
    queryKey: [model, currentPage, modelId],
    queryFn: () =>
      reportsServiceHandlers.loadRecords({
        page: currentPage,
        modelId,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
  })

  const loadRecord = useQuery<TDailyReport, AxiosError>({
    queryKey: [model, modelChildId],
    queryFn: () =>
      moduleServiceDailyReportHandlers.loadRecord({ modelChildId }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => {
      errorHttpNotification(error)
    },
    onSuccess: (data) => {
      options.handlers?.onLoadRecord?.(data)
    },
  })

  const deleteRecord = useMutation<void, AxiosError, TDailyReport>(
    (payload) => moduleServiceDailyReportHandlers.deleteRecord(payload),
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TDailyReport, AxiosError, TDailyReport>(
    (payload: TDailyReport) =>
      moduleServiceDailyReportHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TDailyReport, AxiosError, TDailyReport>(
    (payload: TDailyReport) =>
      moduleServiceDailyReportHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadBundlesProject = useQuery<TBundle[], AxiosError>({
    queryKey: ['bundles', modelId],
    queryFn: () => moduleServiceHandlers.loadBundlesProject({ modelId }),
    onError: (error) => errorHttpNotification(error),
    enabled: enableQuery?.loadBundlesProject,
    refetchOnWindowFocus: false,
  })

  const actionRecord = useMutation<TDailyReport, AxiosError, TRecordAction>(
    (payload: TRecordAction) =>
      moduleServiceDailyReportHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadCoilStocks = useQuery<TCoils, AxiosError>({
    queryKey: ['coilStocks', filters?.coilStocks],
    queryFn: () =>
      coilStocksHandlers.loadCatalog({
        dynamicFilters: filters?.coilStocks,
      }),
    onError: (error) => errorHttpNotification(error),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.purchaseOrdersCatalog,
  })

  return {
    addRecord,
    editRecord,
    loadRecord,
    loadBundlesProject,
    actionRecord,
    loadCoilStocks,
    deleteRecord,
    loadRecords,
  }
}
